import { Settings, X, Minimize2, Maximize2 } from 'lucide-react'
import { mtfLabel, tfSeconds } from '@/utils/mtf'

export default function CHoCHBOSQuantumBadge({
  result,
  onOpenSettings,
  onClose,
  onMinimize,
  minimized = false,
  version = '1.0',
  onHeaderMouseDown,
}) {
  const data = result || {}
  // Los contadores y eventos del badge solo miran el TF PROPIO del chart; las
  // líneas HTF (marcadas con `_mtf` por el merge MTF) van en su línea aparte.
  const lines = (data.lines || []).filter(l =>
    ['BOS', 'CHoCH', 'INTERNAL_BOS', 'INTERNAL_CHoCH'].includes(l.type) && !l._mtf
  )
  const swing = lines.filter(l => l.type === 'BOS' || l.type === 'CHoCH')
  const internal = lines.filter(l => l.type === 'INTERNAL_BOS' || l.type === 'INTERNAL_CHoCH')
  const lastStructure = lines[lines.length - 1]
  const trend = lastStructure?.direction === 'bull' ? 'bull' : lastStructure?.direction === 'bear' ? 'bear' : 'neutral'
  const trendColor = trend === 'bull' ? '#089981' : trend === 'bear' ? '#f23645' : '#B08D57'

  // Contadores 1:1 con el canvas: si el render expone lo pintado, manda eso.
  const painted = data._paintedCounts || null
  const countSwing = painted ? painted.swing : swing.length
  const countInternal = painted ? painted.internal : internal.length

  // Línea MTF: solo si hay TFs adicionales activos (lo marca el merge con
  // `_mtfTimeframes`). Conteos de líneas HTF realmente pintadas, por TF.
  const mtfTfs = [...(data._mtfTimeframes || [])].sort((a, b) => (tfSeconds(a) ?? 0) - (tfSeconds(b) ?? 0))
  const htfLine = mtfTfs.map((tf) => `${mtfLabel(tf)} ×${painted?.htf?.[tf] ?? 0}`).join(' · ')

  return (
    <div
      className="absolute top-2 left-2 z-10 flex flex-col gap-1 rounded-lg backdrop-blur-sm shadow-lg overflow-hidden min-w-[220px]"
      style={{ background: 'var(--paper, #FDFDFA)', border: '1px solid var(--hair, rgba(176,141,87,0.35))' }}
    >
      <div
        className="flex items-center gap-1.5 px-2.5 pt-2 pb-1 cursor-grab active:cursor-grabbing"
        onMouseDown={onHeaderMouseDown}
      >
        <span
          className="text-[11px] font-semibold tracking-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", color: 'var(--green-deep, #0B3D2E)' }}
        >
          CHoCH / BOS Quantum <span className="opacity-60 font-normal">[{version}]</span>
          {minimized && (
            <span className="ml-1.5 text-[10px]" style={{ color: trendColor }}>· {trend.toUpperCase()}</span>
          )}
        </span>
        <button onClick={onOpenSettings} title="Configuración" className="ml-auto p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <Settings size={12} />
        </button>
        {minimized ? (
          <button onClick={onMinimize} title="Expandir" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Maximize2 size={12} />
          </button>
        ) : (
          <button onClick={onMinimize} title="Minimizar" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
            <Minimize2 size={12} />
          </button>
        )}
        <button onClick={onClose} title="Ocultar dashboard" className="p-0.5 transition-colors rounded hover:bg-black/5" style={{ color: 'var(--ink, #15211B)' }}>
          <X size={12} />
        </button>
      </div>

      {!minimized && (
        <>
          <div className="px-2.5 pb-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Sesgo estructural</span>
              <span className="text-[11px] font-semibold" style={{ color: trendColor }}>{trend.toUpperCase()}</span>
            </div>
          </div>

          <div className="flex flex-col gap-0.5 px-2.5 pb-1">
            <CountBar label="Swing" value={countSwing} color={trendColor} />
            <CountBar label="Intern" value={countInternal} color={trendColor} />
          </div>

          {mtfTfs.length > 0 && (
            <div className="flex items-center justify-between px-2.5 pb-1">
              <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>HTF</span>
              <span className="text-[10px] font-medium tabular-nums" style={{ color: 'var(--ink-soft, #4A5A52)' }}>{htfLine}</span>
            </div>
          )}

          {lastStructure && (
            <div className="px-2.5 pb-1" style={{ borderTop: '1px solid var(--hair, rgba(176,141,87,0.35))' }}>
              <div className="pt-1 flex items-center justify-between">
                <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>Último evento</span>
                <span className="text-[10px] font-medium" style={{ color: trendColor }}>
                  {lastStructure.type} {lastStructure.direction?.toUpperCase()}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function CountBar({ label, value, color }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px]" style={{ color: 'var(--ink, #15211B)' }}>{label}</span>
      <span className="text-[10px] font-medium tabular-nums" style={{ color }}>{value}</span>
    </div>
  )
}
